/**
 * «واریز رمزارز» review queue (Phase 5; PRODUCT_REQUIREMENTS §8.2, OD-20). Oldest registered transfer
 * first. The admin checks the explorer and:
 *   EVIDENCE → records senders, on-chain amount, block time (append-only) → UNDER_REVIEW + screening;
 *   APPROVE  → final amount = on-chain evidence, credited exactly once; a REVIEW screening result needs
 *              explicit acceptance; HIGH_RISK / SANCTIONS / ERROR block; below $5 or no price needs an
 *              explicit decision;
 *   REJECT   → reason, no money moves.
 * Every command: step-up + reason + expected version + Idempotency-Key + audit.
 */
import {
  AppContainer,
  Button,
  DataTable,
  PageHeader,
  SelectField,
  StateView,
  TextField,
} from '@hopex/ui';
import { useState } from 'react';
import { api, dt, newIdempotencyKey, type Paged } from '../../api.ts';
import { CommandDialog, Facts } from '../../lib/command.tsx';
import { CRYPTO_DEPOSIT_STATUS, exact, type CryptoDepositRow } from '../../lib/crypto-model.ts';
import { Remote, useRemote } from '../../lib/remote.tsx';
import { Counter, num, Pager, Tone, UserCell } from '../money/shared.tsx';
import { LongId, ScreeningCell } from './shared.tsx';

type Act = 'EVIDENCE' | 'APPROVE' | 'REJECT' | 'RESCREEN';
const LABEL: Record<Act, string> = {
  EVIDENCE: 'ثبت شواهد زنجیره',
  APPROVE: 'تأیید و اعتبار',
  REJECT: 'رد',
  RESCREEN: 'غربالگری دوباره',
};

function ActDialog({
  d,
  act,
  onClose,
  onDone,
}: {
  d: CryptoDepositRow;
  act: Act;
  onClose: () => void;
  onDone: () => void;
}) {
  const [key] = useState(newIdempotencyKey);
  const [senders, setSenders] = useState('');
  const [amount, setAmount] = useState(d.evidence?.amountOnchain ?? '');
  const [blockTime, setBlockTime] = useState('');
  const [acceptReview, setAcceptReview] = useState(false);
  const [acceptBelow, setAcceptBelow] = useState(false);
  const needsAccept = d.screening.state === 'NEEDS_ACCEPTANCE';
  const blocked = d.screening.state === 'BLOCKED';
  const ok =
    act === 'EVIDENCE'
      ? senders.trim().length >= 8 && /^\d+(\.\d+)?$/.test(amount)
      : act === 'APPROVE'
        ? !blocked && /^\d+(\.\d+)?$/.test(amount) && (!needsAccept || acceptReview)
        : true;
  return (
    <CommandDialog
      title={`${LABEL[act]} — ${d.ref}`}
      tone={act === 'APPROVE' ? 'positive' : act === 'REJECT' ? 'danger' : 'primary'}
      confirmLabel={LABEL[act]}
      canSubmit={ok}
      reasonRequired={act !== 'RESCREEN'}
      run={async (reason) => {
        if (act === 'RESCREEN') return void (await api.post(`/crypto-deposits/${d.id}/rescreen`));
        if (act === 'EVIDENCE')
          return void (await api.post(
            `/crypto-deposits/${d.id}/evidence`,
            {
              senderAddresses: senders.split(/[\s,]+/).filter(Boolean),
              amountOnchain: amount,
              ...(blockTime ? { blockTime: new Date(blockTime).toISOString() } : {}),
              expectedVersion: d.version,
              reason,
            },
            { 'idempotency-key': key },
          ));
        if (act === 'APPROVE')
          return void (await api.post(
            `/crypto-deposits/${d.id}/approve`,
            {
              finalAmount: amount,
              expectedVersion: d.version,
              reason,
              acceptScreeningReview: acceptReview,
              acceptBelowMinimum: acceptBelow,
            },
            { 'idempotency-key': key },
          ));
        await api.post(
          `/crypto-deposits/${d.id}/reject`,
          { expectedVersion: d.version, reason },
          { 'idempotency-key': key },
        );
      }}
      onClose={onClose}
      onDone={onDone}
      data-crypto-deposit-action={act}
    >
      <Facts
        rows={[
          ['کاربر', d.displayName ?? d.publicId],
          ['دارایی / شبکه', `${d.asset} · ${d.networkName}`],
          ['آدرس مقصد هوپکس', <LongId key="a" value={d.address} />],
          ['TXID', <LongId key="t" value={d.txid} href={d.explorerUrl} />],
          ...(d.evidence
            ? ([
                [
                  'مقدار روی زنجیره',
                  <bdi key="e" dir="ltr" className="font-mono">
                    {exact(d.evidence.amountOnchain, d.asset)}
                  </bdi>,
                ],
              ] as [string, React.ReactNode][])
            : []),
          ['غربالگری', <ScreeningCell key="s" s={d.screening} />],
        ]}
      />
      {act === 'EVIDENCE' && (
        <div className="grid gap-2">
          <TextField
            label="آدرس(های) فرستنده"
            value={senders}
            onChange={setSenders}
            ltr
            description="با فاصله یا ویرگول جدا کنید."
          />
          <div className="grid gap-2 sm:grid-cols-2">
            <TextField
              label={`مقدار روی زنجیره (${d.asset})`}
              value={amount}
              onChange={setAmount}
              ltr
            />
            <TextField
              label="زمان بلاک (اختیاری)"
              value={blockTime}
              onChange={setBlockTime}
              ltr
              description="ISO، مثلاً 2026-10-06T12:30:00Z"
            />
          </div>
        </div>
      )}
      {act === 'APPROVE' && (
        <div className="grid gap-2">
          <TextField
            label={`مقدار نهایی (${d.asset}) — باید با شواهد برابر باشد`}
            value={amount}
            onChange={setAmount}
            ltr
          />
          {blocked && (
            <p className="m-0 text-[13px] text-negative" data-screening-blocked>
              تأیید ممکن نیست: نتیجه‌ی غربالگری اجازه‌ی اعتبار نمی‌دهد ({d.screening.reason}).
            </p>
          )}
          {needsAccept && (
            <label className="flex items-start gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={acceptReview}
                onChange={(e) => setAcceptReview(e.target.checked)}
                className="mt-1"
                data-accept-review
              />
              نتیجه‌ی «نیازمند بررسی» را بررسی کردم و با همین دلیل می‌پذیرم (تصمیم با نسخه‌ی سیاست
              ثبت می‌شود).
            </label>
          )}
          <label className="flex items-start gap-2 text-[13px]">
            <input
              type="checkbox"
              checked={acceptBelow}
              onChange={(e) => setAcceptBelow(e.target.checked)}
              className="mt-1"
              data-accept-below
            />
            اگر معادل کمتر از ۵ دلار باشد یا قیمت در دسترس نباشد، اعتبار را آگاهانه تأیید می‌کنم.
          </label>
        </div>
      )}
    </CommandDialog>
  );
}

const ACTS: Record<string, Act[]> = {
  TXID_SUBMITTED: ['EVIDENCE', 'REJECT'],
  UNDER_REVIEW: ['APPROVE', 'EVIDENCE', 'RESCREEN', 'REJECT'],
};

export function CryptoDepositsPage() {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<{ d: CryptoDepositRow; act: Act } | null>(null);
  const q = new URLSearchParams({ page: String(page) });
  if (status) q.set('status', status);
  const r = useRemote<Paged<CryptoDepositRow>>(`/crypto-deposits?${q.toString()}`);
  return (
    <AppContainer>
      <PageHeader title="واریز رمزارز" />
      <div className="mb-3 max-w-xs">
        <SelectField
          label="وضعیت"
          value={status}
          onChange={(v) => (setStatus(v), setPage(1))}
          options={[
            { value: '', label: 'در انتظار بررسی' },
            ...Object.entries(CRYPTO_DEPOSIT_STATUS).map(([k, v]) => ({
              value: k,
              label: v.label,
            })),
          ]}
        />
      </div>
      <Remote r={r}>
        {(d) => (
          <div className="flex flex-col gap-3" data-crypto-deposit-queue>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
              {Object.entries(CRYPTO_DEPOSIT_STATUS).map(([k, v]) => (
                <Counter key={k} label={v.label} value={num(d.counts?.[k])} />
              ))}
            </div>
            <DataTable<CryptoDepositRow>
              label="واریزهای رمزارز"
              rows={d.items}
              density="compact"
              stickyFirst
              columns={[
                {
                  id: 'ref',
                  header: 'کد',
                  isRowHeader: true,
                  cell: (x) => (
                    <bdi dir="ltr" className="font-mono text-[12px]">
                      {x.ref}
                    </bdi>
                  ),
                },
                {
                  id: 'user',
                  header: 'کاربر',
                  cell: (x) => (
                    <UserCell
                      userId={x.userId}
                      name={x.displayName}
                      publicId={x.publicId}
                      tab="crypto"
                    />
                  ),
                },
                {
                  id: 'asset',
                  header: 'دارایی / شبکه',
                  cell: (x) => (
                    <span className="flex flex-col text-[12px]">
                      <bdi dir="ltr" className="font-semibold">
                        {x.asset}
                      </bdi>
                      <span className="text-fg-subtle">{x.networkName}</span>
                      {x.memo && (
                        <span className="text-fg-subtle">
                          ممو: <bdi dir="ltr">{x.memo}</bdi>
                        </span>
                      )}
                    </span>
                  ),
                },
                {
                  id: 'txid',
                  header: 'TXID',
                  cell: (x) => (
                    <span className="flex flex-col gap-0.5 text-[12px]">
                      <LongId value={x.txid} href={x.explorerUrl} />
                      {x.lateRegistration && (
                        <span className="text-accent-text" data-late>
                          ثبت پس از مهلت
                        </span>
                      )}
                    </span>
                  ),
                },
                {
                  id: 'amount',
                  header: 'مقدار',
                  numeric: true,
                  cell: (x) => (
                    <bdi dir="ltr" className="font-mono text-[12px]">
                      {exact(x.finalAmount ?? x.evidence?.amountOnchain ?? null, x.asset)}
                    </bdi>
                  ),
                },
                {
                  id: 'screen',
                  header: 'غربالگری',
                  cell: (x) => <ScreeningCell s={x.screening} />,
                },
                {
                  id: 'st',
                  header: 'وضعیت',
                  cell: (x) => (
                    <span className="flex flex-col items-start gap-0.5 text-[12px]">
                      <Tone map={CRYPTO_DEPOSIT_STATUS} code={x.status} />
                      {x.minimumCheck && x.minimumCheck !== 'MET' && (
                        <span className="text-fg-subtle">زیر هدف ۵ دلار / بدون قیمت</span>
                      )}
                      {x.rejectReason && <span className="text-fg-subtle">{x.rejectReason}</span>}
                      <span className="text-fg-subtle">{dt(x.txidSubmittedAt ?? x.createdAt)}</span>
                    </span>
                  ),
                },
                {
                  id: 'act',
                  header: 'عملیات',
                  cell: (x) => (
                    <div className="flex flex-wrap gap-1">
                      {(ACTS[x.status] ?? []).map((a) => (
                        <Button
                          key={a}
                          size="sm"
                          variant={a === 'APPROVE' ? 'secondary' : 'ghost'}
                          onPress={() => setOpen({ d: x, act: a })}
                          data-crypto-deposit-act={`${x.ref}:${a}`}
                        >
                          {LABEL[a]}
                        </Button>
                      ))}
                    </div>
                  ),
                },
              ]}
              empty={<StateView compact kind="empty" title="واریز رمزارزی با این فیلتر نیست" />}
            />
            <Pager page={d.page} hasMore={d.hasMore} onPage={setPage} />
          </div>
        )}
      </Remote>
      {open && (
        <ActDialog
          d={open.d}
          act={open.act}
          onClose={() => setOpen(null)}
          onDone={() => (setOpen(null), r.reload())}
        />
      )}
    </AppContainer>
  );
}
